import Grid from "@bmi-digital/components/grid";
import MediaGallery from "@bmi-digital/components/media-gallery";
import Section from "@bmi-digital/components/section";
import Thumbnail from "@bmi-digital/components/thumbnail";
import Typography from "@bmi-digital/components/typography";
import { replaceSpaces } from "@bmi-digital/components/utils";
import { microCopy } from "@bmi/microcopies";
import { graphql } from "gatsby";
import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import withGTM from "../utils/google-tag-manager";
import { transformMediaSrc } from "../utils/media";
import RichText from "./RichText";
import { useSiteContext } from "./Site";
import {
  StyledDescription,
  StyledGridItem
} from "./styles/MediaGallerySection.styles";
import type { RichTextData } from "./RichText";
import type { ThumbnailProps } from "@bmi-digital/components/thumbnail";
import type { PayloadMediaGallerySection } from "../types/payload";
import type { ImageGallerySlide } from "./image/types";
import type { VideoGallerySlide } from "./video/types";

export type MediaGallerySectionData = Pick<
  PayloadMediaGallerySection,
  "blockType" | "title"
> & {
  mediaGallerySectionDescription: RichTextData | null;
  medias: readonly { value: ImageGallerySlide | VideoGallerySlide }[];
};

const GTMThumbnail = withGTM<ThumbnailProps>(Thumbnail, {
  label: "altText",
  action: "imageSource"
});

const ThumbnailComponent = (props: ThumbnailProps) => (
  <GTMThumbnail gtm={{ id: "media-gallery1" }} {...props} />
);

const IntegratedMediaGallerySection = ({
  data
}: {
  data: MediaGallerySectionData;
}) => {
  const { title, mediaGallerySectionDescription, medias } = data;
  const { getMicroCopy } = useSiteContext();
  const theme = useTheme();

  const media = useMemo(
    () =>
      transformMediaSrc(
        medias.map((media) => media.value),
        theme
      ),
    [medias, theme]
  );

  return (
    <Section
      backgroundColor="alabaster"
      data-testid={`media-gallery-section-${replaceSpaces(data.title)}`}
    >
      <Grid container>
        <StyledGridItem xs={12} lg={8}>
          {title && (
            <Typography variant="h2" hasUnderline>
              {title}
            </Typography>
          )}
          {mediaGallerySectionDescription &&
          mediaGallerySectionDescription.root.children.length ? (
            <StyledDescription>
              <RichText
                document={mediaGallerySectionDescription}
                hasNoBottomMargin
              />
            </StyledDescription>
          ) : undefined}
        </StyledGridItem>
        <StyledGridItem xs={12} py={0}>
          <MediaGallery
            media={media}
            mediaSize="cover"
            thumbnailComponent={ThumbnailComponent}
            videoButtonLabel={getMicroCopy(microCopy.MEDIA_VIDEO)}
            visualiserButtonLabel={getMicroCopy(microCopy.MEDIA_3D)}
            visualiserText={getMicroCopy(microCopy.MEDIA_VISUALIZER_TEXT)}
          />
        </StyledGridItem>
      </Grid>
    </Section>
  );
};

export default IntegratedMediaGallerySection;

export const query = graphql`
  fragment MediaGallerySectionFragment on PayloadMediaGallerySection {
    blockType
    title
    mediaGallerySectionDescription: description {
      ...RichTextFragment
    }
    medias {
      value {
        ...ImageGallerySlideFragment
        ...VideoGallerySlideFragment
      }
    }
  }
`;
